import type { MockPB, Style } from './types';

// Generatore deterministico PB per ogni atleta.
// Ogni PB ha anche tempo "previous" per calcolare il trend Δ.

export interface MockPBWithTrend extends MockPB {
    previousTimeMs?: number;
    previousTime?: string;
    deltaMs?: number; // negativo = migliorato
}

function hashSeed(seed: string): number {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return (h >>> 0) / 4294967295;
}

function fmtTime(ms: number) {
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    const cs = Math.floor((ms % 1000) / 10);
    return m > 0
        ? `${m}:${String(s).padStart(2, '0')}.${String(cs).padStart(2, '0')}`
        : `${s}.${String(cs).padStart(2, '0')}`;
}

// Definizioni stile/distanza con tempi base (vasca 50m maschio seniores)
interface Event {
    distance: number;
    style: Style;
    baseMs: number;
}
const EVENTS: Event[] = [
    { distance: 50, style: 'SL', baseMs: 27_500 },
    { distance: 100, style: 'SL', baseMs: 60_000 },
    { distance: 200, style: 'SL', baseMs: 128_000 },
    { distance: 400, style: 'SL', baseMs: 270_000 },
    { distance: 800, style: 'SL', baseMs: 560_000 },
    { distance: 1500, style: 'SL', baseMs: 1_080_000 },
    { distance: 50, style: 'DO', baseMs: 31_000 },
    { distance: 100, style: 'DO', baseMs: 66_500 },
    { distance: 200, style: 'DO', baseMs: 142_000 },
    { distance: 50, style: 'RA', baseMs: 34_000 },
    { distance: 100, style: 'RA', baseMs: 74_000 },
    { distance: 200, style: 'RA', baseMs: 160_000 },
    { distance: 50, style: 'FA', baseMs: 29_500 },
    { distance: 100, style: 'FA', baseMs: 64_000 },
    { distance: 200, style: 'FA', baseMs: 140_000 },
    { distance: 200, style: 'MX', baseMs: 140_000 },
    { distance: 400, style: 'MX', baseMs: 290_000 }
];

// Categoria → moltiplicatore tempo (Ragazzi più lenti, Seniores base)
const CATEGORY_FACTOR: Record<string, number> = {
    R: 1.12, // Ragazzi
    C: 1.06, // Cadetti
    J: 1.02, // Juniores
    S: 1.0, // Seniores
    A: 0.98 // Assoluti (elite)
};

// Genere → moltiplicatore (F ~9-11% più lento di M)
const GENDER_FACTOR = { M: 1.0, F: 1.09 };

// Pool → moltiplicatore (25m più veloce 2-3% per via delle virate)
const POOL_FACTOR = { '50': 1.0, '25': 0.975 };

function athleteEventCount(seed: number): number {
    // 5-10 eventi per atleta (specialista preferisce 4-5 distanze)
    return 5 + Math.floor(seed * 6);
}

export function generatePBsForAthlete(
    athleteId: string,
    category: string,
    gender: 'M' | 'F',
    _year: number
): MockPBWithTrend[] {
    const catFactor = CATEGORY_FACTOR[category] ?? 1.04;
    const gFactor = GENDER_FACTOR[gender];
    const seed0 = hashSeed(athleteId + 'pb0');

    // Seleziona set di eventi preferiti (specialità)
    const nEvents = athleteEventCount(seed0);
    const eventPool = [...EVENTS];
    const shuffled = eventPool.sort(
        (a, b) => hashSeed(athleteId + a.style + a.distance) - hashSeed(athleteId + b.style + b.distance)
    );
    const selectedEvents = shuffled.slice(0, nEvents);

    const pbs: MockPBWithTrend[] = [];

    selectedEvents.forEach((e, idx) => {
        // Genera PB per vasca 50 e 25 se rilevante (1500 solo 50)
        const pools: ('50' | '25')[] = e.distance === 1500 ? ['50'] : ['50', '25'];
        pools.forEach(pool => {
            const poolFactor = POOL_FACTOR[pool];
            const perfSeed = hashSeed(athleteId + e.style + e.distance + pool);
            // Variabilità individuale ±4%
            const indivFactor = 0.96 + perfSeed * 0.08;
            const currentMs = Math.round(e.baseMs * catFactor * gFactor * poolFactor * indivFactor);

            // Previous PB: 2-6% più lento (miglioramento recente)
            const improvement = 0.97 + hashSeed(athleteId + e.style + 'prev') * 0.05; // 2-5% miglior.
            const previousMs = Math.round(currentMs / improvement);
            const deltaMs = currentMs - previousMs;

            // FINA points approssimativo (scala inversa)
            const finaPoints = Math.round(500 + (1 - indivFactor) * 400 + perfSeed * 100);

            const daysAgo = Math.floor(10 + perfSeed * 180); // PB fatto tra 10 e 190 giorni fa
            const d = new Date('2026-04-22');
            d.setDate(d.getDate() - daysAgo);
            const dateStr = d.toISOString().slice(0, 10);

            pbs.push({
                id: `pb-${athleteId}-${e.style}-${e.distance}-${pool}`,
                athleteId,
                distance: e.distance,
                style: e.style,
                pool,
                timeMs: currentMs,
                time: fmtTime(currentMs),
                finaPoints: Math.max(300, Math.min(950, finaPoints)),
                date: dateStr,
                eventName: pickEventName(idx, perfSeed),
                rank: 1 + Math.floor(perfSeed * 15),
                previousTimeMs: previousMs,
                previousTime: fmtTime(previousMs),
                deltaMs
            });
        });
    });

    // Ordina per stile, distanza, pool
    return pbs.sort((a, b) => {
        if (a.style !== b.style) return a.style.localeCompare(b.style);
        if (a.distance !== b.distance) return a.distance - b.distance;
        return a.pool.localeCompare(b.pool);
    });
}

const EVENT_NAMES = [
    'Campionato Regionale Lazio',
    'Meeting Città di Roma',
    'Trofeo Primavera',
    'Campionati Italiani Assoluti',
    'Criteria Nazionali Giovanili',
    'Coppa Italia',
    'Meeting del Tiberino',
    'Trofeo Fausto Rudelli'
];

function pickEventName(idx: number, seed: number): string {
    return EVENT_NAMES[(idx + Math.floor(seed * 7)) % EVENT_NAMES.length];
}

// Export atleta self (usato da alcune pagine atleta placeholder)
export const mockPBs = generatePBsForAthlete('athlete-self', 'C', 'F', 2008);

export function pbsByAthlete(
    athleteId: string,
    category = 'C',
    gender: 'M' | 'F' = 'F',
    year = 2008
): MockPBWithTrend[] {
    return generatePBsForAthlete(athleteId, category, gender, year);
}
